import React from "react";
import { Stack, Box, Typography, alpha } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

import FormAlert from "../components/common/FormAlert";
import FormSelectField from "../components/common/FormSelectField";
import FormTextField from "../components/common/FormTextField";
import FormSwitchField from "../components/common/FormSwitchField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import { updateBranchSettings } from "../redux/actions/settingsActions";
import { settingsActions } from "../redux/slices/settings";
import { BranchSettingsResponseDto } from "../shared/dtos/branchSettings/BranchSettingsResponseDto";
import { BranchSettingsRequestDto } from "../shared/dtos/branchSettings/BranchSettingsRequestDto";

const TIMEZONES = [
  { value: "Africa/Nairobi", label: "Africa/Nairobi (EAT)" },
  { value: "Africa/Kampala", label: "Africa/Kampala (EAT)" },
  { value: "Africa/Dar_es_Salaam", label: "Africa/Dar_es_Salaam (EAT)" },
  { value: "Africa/Johannesburg", label: "Africa/Johannesburg (SAST)" },
  { value: "Africa/Lagos", label: "Africa/Lagos (WAT)" },
  { value: "Europe/London", label: "Europe/London (GMT/BST)" },
  { value: "UTC", label: "UTC" },
];

const settingsSchema = Yup.object().shape({
  timezone: Yup.string().required("Timezone is required"),
  paymentIntegration: Yup.object().shape({
    active: Yup.boolean().required(),
    gatewayApiKey: Yup.string().nullable(),
    gatewayWebhookSecret: Yup.string().nullable(),
    allowsMpesaStk: Yup.boolean().required(),
    allowsMpesaC2b: Yup.boolean().required(),
    allowsBankIpn: Yup.boolean().required(),
  }),
  communicationIntegration: Yup.object().shape({
    active: Yup.boolean().required(),
    gatewayApiKey: Yup.string().nullable(),
    gatewayWebhookSecret: Yup.string().nullable(),
    allowsSms: Yup.boolean().required(),
    allowsEmail: Yup.boolean().required(),
    allowsWhatsapp: Yup.boolean().required(),
    allowsUssd: Yup.boolean().required(),
  }),
});

interface UpdateBranchSettingsFormProps {
  branchSettings: BranchSettingsResponseDto | null;
  loading: boolean;
  error?: string | null;
}

interface FormValues {
  timezone: string;
  paymentIntegration: {
    active: boolean;
    gatewayApiKey: string;
    gatewayWebhookSecret: string;
    allowsMpesaStk: boolean;
    allowsMpesaC2b: boolean;
    allowsBankIpn: boolean;
    hasGatewayApiKey: boolean;
  };
  communicationIntegration: {
    active: boolean;
    gatewayApiKey: string;
    gatewayWebhookSecret: string;
    allowsSms: boolean;
    allowsEmail: boolean;
    allowsWhatsapp: boolean;
    allowsUssd: boolean;
    hasGatewayApiKey: boolean;
  };
}

const UpdateBranchSettingsForm: React.FC<UpdateBranchSettingsFormProps> = ({
  branchSettings,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const initialValues: FormValues = {
    timezone: branchSettings?.timezone || "Africa/Nairobi",
    paymentIntegration: {
      active: branchSettings?.paymentIntegration?.active ?? false,
      gatewayApiKey: "",
      gatewayWebhookSecret: "",
      allowsMpesaStk:
        branchSettings?.paymentIntegration?.allowsMpesaStk ?? false,
      allowsMpesaC2b:
        branchSettings?.paymentIntegration?.allowsMpesaC2b ?? false,
      allowsBankIpn: branchSettings?.paymentIntegration?.allowsBankIpn ?? false,
      hasGatewayApiKey:
        branchSettings?.paymentIntegration?.hasGatewayApiKey ?? false,
    },
    communicationIntegration: {
      active: branchSettings?.communicationIntegration?.active ?? false,
      gatewayApiKey: "",
      gatewayWebhookSecret: "",
      allowsSms: branchSettings?.communicationIntegration?.allowsSms ?? false,
      allowsEmail:
        branchSettings?.communicationIntegration?.allowsEmail ?? false,
      allowsWhatsapp:
        branchSettings?.communicationIntegration?.allowsWhatsapp ?? false,
      allowsUssd: branchSettings?.communicationIntegration?.allowsUssd ?? false,
      hasGatewayApiKey:
        branchSettings?.communicationIntegration?.hasGatewayApiKey ?? false,
    },
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() =>
          dispatch(settingsActions.setErrorUpdateBranchSettings(null))
        }
      />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={settingsSchema}
        onSubmit={(values) => {
          const payload: BranchSettingsRequestDto = {
            timezone: values.timezone,
            paymentIntegration: {
              active: values.paymentIntegration.active,
              allowsMpesaStk: values.paymentIntegration.allowsMpesaStk,
              allowsMpesaC2b: values.paymentIntegration.allowsMpesaC2b,
              allowsBankIpn: values.paymentIntegration.allowsBankIpn,
              gatewayApiKey:
                values.paymentIntegration.gatewayApiKey || undefined,
              gatewayWebhookSecret:
                values.paymentIntegration.gatewayWebhookSecret || undefined,
            },
            communicationIntegration: {
              active: values.communicationIntegration.active,
              allowsSms: values.communicationIntegration.allowsSms,
              allowsEmail: values.communicationIntegration.allowsEmail,
              allowsWhatsapp: values.communicationIntegration.allowsWhatsapp,
              allowsUssd: values.communicationIntegration.allowsUssd,
              gatewayApiKey:
                values.communicationIntegration.gatewayApiKey || undefined,
              gatewayWebhookSecret:
                values.communicationIntegration.gatewayWebhookSecret ||
                undefined,
            },
          };

          dispatch(updateBranchSettings(payload));
        }}
      >
        {({ values, dirty, isValid }: FormikProps<FormValues>) => (
          <Form>
            <Stack spacing={3}>
              <FormSelectField
                name="timezone"
                label="Regional Timezone"
                options={TIMEZONES}
              />

              {/* Payment Gateway Box */}
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: (t) => alpha(t.palette.background.default, 0.4),
                }}
              >
                <Stack
                  direction="row"
                  sx={{
                    mb: 2,
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="overline" sx={{ fontWeight: 700 }}>
                    Payment Integration Gateway
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {values.paymentIntegration.hasGatewayApiKey ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <ErrorIcon color="error" fontSize="small" />
                    )}
                    <Typography variant="caption">
                      {values.paymentIntegration.hasGatewayApiKey
                        ? "Keys Configured"
                        : "Keys Missing"}
                    </Typography>
                  </Box>
                </Stack>

                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="paymentIntegration.gatewayApiKey"
                    label="API Key"
                    type="password"
                    placeholder="••••••••"
                  />
                  <FormTextField
                    name="paymentIntegration.gatewayWebhookSecret"
                    label="Webhook Secret"
                    type="password"
                    placeholder="••••••••"
                  />
                </Box>

                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ mt: 2, flexWrap: "wrap" }}
                >
                  <FormSwitchField
                    name="paymentIntegration.active"
                    label="Active"
                  />
                  <FormSwitchField
                    name="paymentIntegration.allowsMpesaStk"
                    label="M-Pesa STK"
                  />
                  <FormSwitchField
                    name="paymentIntegration.allowsMpesaC2b"
                    label="M-Pesa C2B"
                  />
                  <FormSwitchField
                    name="paymentIntegration.allowsBankIpn"
                    label="Bank IPN"
                  />
                </Stack>
              </Box>

              {/* Communication Gateway Box */}
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: (t) => alpha(t.palette.background.default, 0.4),
                }}
              >
                <Stack
                  direction="row"
                  sx={{
                    mb: 2,
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="overline" sx={{ fontWeight: 700 }}>
                    Communication Integration Hub
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {values.communicationIntegration.hasGatewayApiKey ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <ErrorIcon color="error" fontSize="small" />
                    )}
                    <Typography variant="caption">
                      {values.communicationIntegration.hasGatewayApiKey
                        ? "Keys Configured"
                        : "Keys Missing"}
                    </Typography>
                  </Box>
                </Stack>

                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="communicationIntegration.gatewayApiKey"
                    label="Gateway API Key"
                    type="password"
                    placeholder="••••••••"
                  />
                  <FormTextField
                    name="communicationIntegration.gatewayWebhookSecret"
                    label="Webhook Secret"
                    type="password"
                    placeholder="••••••••"
                  />
                </Box>

                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ mt: 2, flexWrap: "wrap" }}
                >
                  <FormSwitchField
                    name="communicationIntegration.active"
                    label="Active"
                  />
                  <FormSwitchField
                    name="communicationIntegration.allowsSms"
                    label="SMS"
                  />
                  <FormSwitchField
                    name="communicationIntegration.allowsWhatsapp"
                    label="WhatsApp"
                  />
                  <FormSwitchField
                    name="communicationIntegration.allowsEmail"
                    label="Email"
                  />
                  <FormSwitchField
                    name="communicationIntegration.allowsUssd"
                    label="USSD"
                  />
                </Stack>
              </Box>

              <SubmitButton
                title="Save Branch Settings"
                loading={loading}
                loadingText="Saving..."
                disabled={!dirty || !isValid || loading}
                startIcon={<SaveOutlinedIcon />}
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default UpdateBranchSettingsForm;
